/* eslint-disable react-hooks/exhaustive-deps */
import React, { Children, useEffect, useState } from 'react';
import { listStyles } from './section-list-with-search-field-component-helper';
import { Grid, Icon, InputAdornment, Typography } from '@mui/material';
import { Divider, TextField } from '@biq/apex-ui';
import SearchIcon from '@mui/icons-material/Search';
import CancelIcon from '@mui/icons-material/Cancel';
import { SectionListEmptyError } from './SectionListEmptyError';
import { SectionListSearchEmpty } from './SectionListSearchEmpty';

interface SectionListWithSearchFieldProps {
    listData: Array<any>;
    textFieldLabel: string;
    listDataError: any;
    sectionTitleKey: string;
    searchKeys: string[];
    renderListItemComponent: (value: any) => React.ReactNode;
    emptyErrorHeadingText: string;
    emptyErrorSubHeadingText: string;
    searchEmptyHeadingText: string;
    searchEmptySubHeadingText: string;
}

export const SectionListWithSearchField = ({
    listData,
    textFieldLabel,
    listDataError,
    sectionTitleKey,
    searchKeys,
    renderListItemComponent,
    emptyErrorHeadingText,
    emptyErrorSubHeadingText,
    searchEmptyHeadingText,
    searchEmptySubHeadingText,
}: SectionListWithSearchFieldProps): React.JSX.Element => {
    const [filteredData, setFilteredData] = useState<Array<any>>([]);
    const [originalData, setOriginalData] = useState<Array<any>>([]);
    const [textFieldValue, setTextFieldValue] = useState<string>('');

    useEffect(() => {
        const groupedData: any = {};

        listData.forEach((data) => {
            const firstLetter: string = data[sectionTitleKey]
                .charAt(0)
                .toUpperCase();

            if (!groupedData[firstLetter]) {
                groupedData[firstLetter] = [];
            }

            groupedData[firstLetter].push(data);
        });

        const resultArray = Object.entries(groupedData).map(([key, value]) => {
            return { [key]: value };
        });

        resultArray.sort((a, b) =>
            Object.keys(a)[0].localeCompare(Object.keys(b)[0])
        );
        setOriginalData(resultArray);
        setFilteredData(resultArray);
    }, [listData]);

    useEffect(() => {
        const filterItemBySearchKey = (
            item: any,
            searchText: string
        ): boolean => {
            return searchKeys.some((searchKey) => {
                const itemValue = item[searchKey]?.toLowerCase();
                return itemValue?.includes(searchText.toLowerCase());
            });
        };

        const getFilteredItems = (values: any[]): any[] => {
            const searchText = textFieldValue.toLowerCase();
            return values?.filter((item) =>
                filterItemBySearchKey(item, searchText)
            );
        };

        const filteredValues = originalData
            .map((data) => {
                const key = Object.keys(data)[0];
                const values = data[key];
                const filteredItems = getFilteredItems(values);
                return { [key]: filteredItems };
            })
            .filter((item) => {
                const key = Object.keys(item)[0];
                return item[key]?.length > 0;
            });
        setFilteredData(filteredValues);
    }, [textFieldValue]);

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement>
    ): void => {
        setTextFieldValue(e.target.value);
    };

    const isSearchEmpty =
        !listDataError &&
        textFieldValue !== '' &&
        filteredData.length === 0 &&
        originalData.length !== 0;

    return (
        <Grid sx={listStyles.container}>
            <Grid sx={listStyles.textFieldStyles}>
                <TextField
                    variant='outlined'
                    label={textFieldLabel}
                    value={textFieldValue}
                    onChange={handleInputChange}
                    InputProps={{
                        startAdornment: [
                            <InputAdornment key={'searchIcon'} position='start'>
                                <SearchIcon sx={listStyles.searchIconStyles} />
                            </InputAdornment>,
                        ],
                        endAdornment: textFieldValue.length
                            ? [
                                  <Icon
                                      key={'clearIcon'}
                                      fontSize='inherit'
                                      color='disabled'
                                      sx={listStyles.crossIconStyles}
                                      onClick={(): void => {
                                          setTextFieldValue('');
                                      }}
                                  >
                                      <CancelIcon />
                                  </Icon>,
                              ]
                            : null,
                    }}
                />
            </Grid>
            <Grid sx={listStyles.listContainerStyles}>
                {(listDataError || originalData.length === 0) && (
                    <SectionListEmptyError
                        emptyErrorHeadingText={emptyErrorHeadingText}
                        emptyErrorSubHeadingText={emptyErrorSubHeadingText}
                    />
                )}
                {!listDataError &&
                    filteredData.length > 0 &&
                    Children.toArray(
                        filteredData.map((data) => {
                            const key = Object.keys(data)[0];
                            const values = data[key];
                            return (
                                <Grid sx={listStyles.dataContainerStyles}>
                                    <Typography
                                        sx={
                                            listStyles.alphabeticalOrderTextStyles
                                        }
                                    >
                                        {key}
                                    </Typography>
                                    <Divider />
                                    {Children.toArray(
                                        values.map((value: any) =>
                                            renderListItemComponent(value)
                                        )
                                    )}
                                </Grid>
                            );
                        })
                    )}
                {isSearchEmpty && (
                    <SectionListSearchEmpty
                        searchEmptyHeadingText={searchEmptyHeadingText}
                        searchEmptySubHeadingText={searchEmptySubHeadingText}
                    />
                )}
            </Grid>
        </Grid>
    );
};
